/**
 * UnitCo API Error Tracker
 * Intercepts and logs all UnitCo API errors made by the plugin
 */

let isTrackingEnabled = false;
let originalFetch: typeof fetch | null = null;

interface UnitCoApiError {
  url: string;
  status: number;
  statusText?: string;
  errorBody?: any;
  error?: string;
  timestamp: string;
  userAgent: string;
}

/**
 * Initialize global UnitCo API error tracking
 * This should be called once when the app starts
 */
export const initializeUnitCoErrorTracking = () => {
  if (isTrackingEnabled || typeof window === 'undefined') {
    return;
  }

  originalFetch = window.fetch;
  isTrackingEnabled = true;

  // Intercept fetch requests to catch UnitCo API errors
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const [url, options] = args;
    const urlString = typeof url === 'string' ? url : url.toString();
    
    // Only intercept UnitCo API calls
    const isUnitCoApiCall = 
      urlString.includes('unit.sh') || 
      urlString.includes('unit.co') || 
      urlString.includes('api.s.unit') ||
      urlString.includes('api.unit.co');
    
    if (isUnitCoApiCall && originalFetch) {
      try {
        const response = await originalFetch(...args);
        
        // Check for errors (400-599)
        if (!response.ok && response.status >= 400) {
          const clonedResponse = response.clone();
          let errorBody = null;
          
          try {
            errorBody = await clonedResponse.json();
          } catch (e) {
            try {
              errorBody = await clonedResponse.text();
            } catch (e2) {
              // Ignore parsing errors
            }
          }
          
          const errorInfo: UnitCoApiError = {
            url: urlString,
            status: response.status,
            statusText: response.statusText,
            errorBody,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent
          };
          
          // Log detailed error information
          console.error('❌ UnitCo API Error Detected:', errorInfo);
          
          // Dispatch custom event for error tracking
          window.dispatchEvent(new CustomEvent('unitcoApiError', {
            detail: errorInfo
          }));
          
          // Store error in sessionStorage for debugging (last 10 errors)
          try {
            const errorHistory = JSON.parse(sessionStorage.getItem('unitcoApiErrors') || '[]');
            errorHistory.push(errorInfo);
            // Keep only last 10 errors
            if (errorHistory.length > 10) {
              errorHistory.shift();
            }
            sessionStorage.setItem('unitcoApiErrors', JSON.stringify(errorHistory));
          } catch (e) {
            // Ignore storage errors
          }
        }
        
        return response;
      } catch (error: any) {
        const errorInfo: UnitCoApiError = {
          url: urlString,
          status: 0,
          error: error.message,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent
        };
        
        console.error('❌ UnitCo API Request Failed:', errorInfo);
        
        // Dispatch custom event for error tracking
        window.dispatchEvent(new CustomEvent('unitcoApiError', {
          detail: errorInfo
        }));
        
        throw error;
      }
    }
    
    // For non-UnitCo calls, use original fetch
    if (!originalFetch) {
      throw new Error('Fetch is not available');
    }
    return originalFetch(...args);
  };
};

/**
 * Disable UnitCo error tracking (restore original fetch)
 */
export const disableUnitCoErrorTracking = () => {
  if (!isTrackingEnabled || !originalFetch) {
    return;
  }

  window.fetch = originalFetch;
  isTrackingEnabled = false;
  originalFetch = null;
};

/**
 * Get error history from sessionStorage
 */
export const getUnitCoErrorHistory = (): UnitCoApiError[] => {
  try {
    return JSON.parse(sessionStorage.getItem('unitcoApiErrors') || '[]');
  } catch (e) {
    return [];
  }
};

/**
 * Clear error history
 */
export const clearUnitCoErrorHistory = () => {
  try {
    sessionStorage.removeItem('unitcoApiErrors');
  } catch (e) {
    // Ignore
  }
};

